/*
 * Arib Subtitle Stream Controller
 *
 * ARIB字幕ファイルの取得およびWebVTT化、字幕表示機能を実装。
 *
 * hls.jsのTimelineController, SubtitleStreamController,
 * FragmentLoaderを参考にした.
*/
import Event from 'hls.js/lib/events';
import EventHandler from 'hls.js/lib/event-handler';
import {ErrorTypes, ErrorDetails} from 'hls.js/lib/errors';
import {logger} from 'hls.js/lib/utils/logger';
import AribSubtitleXmlParser from './arib-subtitle-xml-parser'
import WebVTTParser from './webvtt-parser'
import moment from 'moment';

const State = {
  STOPPED : 'STOPPED',
  IDLE : 'IDLE',
  KEY_LOADING : 'KEY_LOADING',
  FRAG_LOADING : 'FRAG_LOADING'
};

class AribSubtitleStreamController extends EventHandler {

  constructor(hls) {
    super(hls,
      Event.MANIFEST_LOADED,
      Event.LEVEL_LOADED,
      Event.MEDIA_ATTACHING,
      Event.MEDIA_ATTACHED,
      Event.ERROR,
      Event.KEY_LOADED,
      //Event.FRAG_LOADING,// not call hls's event!
      Event.FRAG_LOADED,
      Event.SUBTITLE_TRACKS_UPDATED,
      Event.SUBTITLE_TRACK_SWITCH,
      Event.SUBTITLE_TRACK_LOADED,
      Event.SUBTITLE_FRAG_PROCESSED,
      Event.INIT_PTS_FOUND);
    this.config = hls.config;
    this.vttFragSNsProcessed = {};
    this.vttFragQueues = {};
    this.currentlyProcessing = null;
    this.state = State.STOPPED;
    this.ticks = 0;
    this.initPTS = undefined;
    this.initPDT = hls.config.initPDT; // 映像のplaylist先頭のProgramDateTime
    this.currentTrackId = this.initPDT !== undefined? 0 : -1;
    this.loaders = {};
    this.tracks = [];
    this.unparsedVttFrags = [];
    this.loadCnt = 0;
    this.preFrag = undefined;
    this.adjustWithPDT = hls.config.adjustWithPDT !== false;
    this.media = hls.config.media;
  }

  destroy() {
    EventHandler.prototype.destroy.call(this);
    this.state = State.STOPPED;
    this.vttFragSNsProcessed = {};
    this.vttFragQueues = {}
    this.currentlyProcessing = null;
    this.loaders = {};
    this.tracks = [];
    this.unparsedVttFrags = [];
    this.loadCnt = 0;
    this.preFrag = undefined;
    this.media = undefined;
  }

  // subtitle-stream-controller
  // Remove all queued items and create a new, empty queue for each track.
  clearVttFragQueues() {
    console.log('clearVttFragQueues');
    this.vttFragQueues = {};
    this.tracks.forEach(track => {
      this.vttFragQueues[track.id] = [];
    });
  }

  // subtitle-stream-controller
  // If no frag is being processed and queue isn't empty, initiate processing of next frag in line.
  nextFrag() {
    //console.log('nextFrag');
    if(this.currentlyProcessing === null && this.currentTrackId > -1 && this.vttFragQueues[this.currentTrackId].length) {
      //if (this.config.useAribSubtitleXml && this.adjustWithPDT && this.vttFragQueues[this.currentTrackId][0].duration === 0) return; //durationが設定するまでは処理しない
      let frag = this.currentlyProcessing = this.vttFragQueues[this.currentTrackId].shift();
      //this.hls.trigger(Event.FRAG_LOADING, {frag: frag});// not call hls's event.
      setTimeout( () => this.loadFrag({frag: frag}), 200); // delay
      this.state = State.FRAG_LOADING;
    }
  }

  // timeline-controller
  // Triggered when an initial PTS is found; used for synchronisation of WebVTT.
  onInitPtsFound(data) {
    if (typeof this.initPTS === 'undefined') {
      this.initPTS = data.initPTS;
      console.log('initPTS', data.initPTS);
    }

    // Due to asynchrony, initial PTS may arrive later than the first VTT fragments are loaded.
    // Parse any unparsed fragments upon receiving the initial PTS.
    if (this.unparsedVttFrags.length) {
      this.unparsedVttFrags.forEach(frag => {
        this.onFragLoaded(frag);
      });
      this.unparsedVttFrags = [];
    }
  }

  //onFragLoading(data) {
  loadFrag(data) {
    let frag = data.frag,
        type = frag.type,
        config = this.hls.config,
        maxRetry,
        timeout,
        retryDelay,
        maxRetryDelay,
        context = { type : type, frag : frag, id: data.id};

    //if (frag.type !== 'arib-subtitle') {
    if (frag.type !== 'subtitle') {
      return;
    }
    //console.log('onFragLoading ', data.frag.type, frag.relurl);
    frag.loaded = 0;
    // if (loader) {
    //   logger.warn(`abort previous fragment loader for type:${type}`);
    //   loader.abort();
    // }

    maxRetry = config.levelLoadingMaxRetry;
    timeout = config.levelLoadingTimeOut;
    retryDelay = config.levelLoadingRetryDelay;
    maxRetryDelay = config.levelLoadingMaxRetryTimeout;
    logger.log(`loading playlist for ${context.type} ${context.level || context.id}`);
    let loader  = this.loaders[context.type] = context.loader = new config.loader(config);
    context.url = frag.url +'?t=' + new Date().getTime();
    context.responseType = 'text';

    let loaderConfig, loaderCallbacks;
    loaderConfig = {timeout, maxRetry, retryDelay, maxRetryDelay};
    loaderCallbacks = { onSuccess : this.loadsuccess.bind(this), onError :this.loaderror.bind(this), onTimeout : this.loadtimeout.bind(this)};
    loader.load(context,loaderConfig,loaderCallbacks);
  }

  loadsuccess(response, stats, context, networkDetails=null) {
    var payload = response.data,
        frag = context.frag;

    //frag.loader = undefined;
    //this.loaders[frag.type] = undefined;

    this.hls.trigger(Event.FRAG_LOADED, {payload: payload, frag: frag, stats: stats, networkDetails: networkDetails});
  }

  loaderror(response, context, networkDetails=null) {
    let loader = context.loader;
    if (loader) {
      loader.abort();
    }
    this.loaders[context.type] = undefined;
    this.hls.trigger(Event.ERROR, {type: ErrorTypes.NETWORK_ERROR, details: ErrorDetails.FRAG_LOAD_ERROR, fatal: false, frag: context.frag, response: response, networkDetails: networkDetails});
  }

  loadtimeout(stats, context, networkDetails=null) {
    let loader = context.loader;
    if (loader) {
      loader.abort();
    }
    this.loaders[context.type] = undefined;
    this.hls.trigger(Event.ERROR, {type: ErrorTypes.NETWORK_ERROR, details: ErrorDetails.FRAG_LOAD_TIMEOUT, fatal: false, frag: context.frag, networkDetails: networkDetails});
  }

  // data will be used for progressive parsing
  loadprogress(stats, context, data, networkDetails=null) { // jshint ignore:line
    let frag = context.frag;
    frag.loaded = stats.loaded;
    this.hls.trigger(Event.FRAG_LOAD_PROGRESS, {frag: frag, stats: stats, networkDetails: networkDetails});
  }
    // When fragment has finished processing, add sn to list of completed if successful.
  onSubtitleFragProcessed(data) {
    console.log('onSubtitleFragProcessed');
    if(data.success) {
      this.vttFragSNsProcessed[data.frag.trackId].push(data.frag.sn);
    }
    this.currentlyProcessing = null;
    this.state = State.IDLE;
    this.nextFrag();
  }

  onManifestLoaded(data) {
    // this.tracks = [];
    // this.unparsedVttFrags = this.unparsedVttFrags || [];
    // this.initPTS = undefined;
    this.initPDT = undefined;
    this.preFrag = undefined;
    console.log('onManifestLoaded');
  }

  onLevelLoaded(data) {
    console.log('onLevelLoaded');
    if (!this.adjustWithPDT) return;
    const details = data.details;
    if (!this.initPDT && details) {
      //this.initPDT = details.programDateTime.getTime();
      //this.initPDT = Date.parse(details.fragments[0].rawProgramDateTime); // not work on Safari;
      this.initPDT = moment(details.fragments[0].rawProgramDateTime).valueOf();
      console.log('initPDT', this.initPDT);

      if (this.unparsedVttFrags.length) {
        this.unparsedVttFrags.forEach(frag => {
          this.onFragLoaded(frag);
        });
        this.unparsedVttFrags = [];
      }
    }
  }
  onMediaAttaching(data) {
    this.media = data.media;
  }

  onMediaAttached(data) {
    this.media = data.media;
    this.state = State.IDLE;
    // console.log('state', 'IDLE')
    // if (this.tracks) {
    //   this.tick();
    // }
    console.log('onMediaAttached');
  }

  // If something goes wrong, procede to next frag, if we were processing one.
  onError(data) {
    let frag = data.frag;
    // don't handle frag error not related to subtitle fragment
    //if (frag && frag.type !== 'arib-subtitle') {
    if (frag && frag.type !== 'subtitle') {
      return;
    }
    if(this.currentlyProcessing) {
      this.currentlyProcessing = null;
      this.nextFrag();
    }
  }

  // subtitle-stream-controller
  tick() {
    this.ticks++;
    if (this.ticks === 1) {
      this.doTick();
      if (this.ticks > 1) {
        setTimeout(() => { this.tick(); }, 1);
      }
      this.ticks = 0;
    }
  }

  // subtitle-stream-controller
  doTick() {
    console.log('doTick');
    switch(this.state) {
      case State.IDLE:
        const tracks = this.tracks;
        let trackId = this.currentTrackId;
        const processedFragSNs = this.vttFragSNsProcessed[trackId],
            fragQueue = this.vttFragQueues[trackId],
            currentFragSN = !!this.currentlyProcessing ? this.currentlyProcessing.sn : -1;
        const alreadyProcessed = function(frag) {
          return processedFragSNs.indexOf(frag.sn) > -1;
        };

        const alreadyInQueue = function(frag) {
          return fragQueue.some(fragInQueue => {return fragInQueue.sn === frag.sn;});
        };

        // exit if tracks don't exist
        if (!tracks) {
          break;
        }
        var trackDetails;
        if (trackId < tracks.length) {
          trackDetails = tracks[trackId].details;
        }
        if (typeof trackDetails === 'undefined') {
          break;
        }
        // Add all fragments that haven't been, aren't currently being and aren't waiting to be processed, to queue.
        trackDetails.fragments.forEach(frag => {
          if(!(alreadyProcessed(frag) || frag.sn === currentFragSN || alreadyInQueue(frag))) {
            // Load key if subtitles are encrypted
            if ((frag.decryptdata && frag.decryptdata.uri != null) && (frag.decryptdata.key == null)) {
              logger.log(`Loading key for ${frag.sn}`);
              this.state = State.KEY_LOADING;
              this.hls.trigger(Event.KEY_LOADING, {frag: frag});
            } else {
              // Frags don't know their subtitle track ID, so let's just add that...
              frag.trackId = trackId;
              //if (!this.config.useAribSubtitleXml) {
                fragQueue.push(frag);
                this.nextFrag();
              // } else {
              //   // set duration
              //   if (this.preFrag) {
              //       this.preFrag.duration =
              //       (moment(frag.programDateTime).valueOf() - moment(this.preFrag.programDateTime).valueOf()) / 1000;
              //     this.nextFrag();
              //   }
              //   fragQueue.push(frag);
              //   this.preFrag = frag;
              // }
            }
          }
        });
        break;
      default: break;
    }
  }

  // Got all new subtitle tracks.
  onSubtitleTracksUpdated(data) {
    console.log('onSubtitleTracksUpdated');
    logger.log('subtitle tracks updated');
    this.tracks = data.subtitleTracks;
    this.clearVttFragQueues();
    this.vttFragSNsProcessed = {};
    this.tracks.forEach(track => {
      this.vttFragSNsProcessed[track.id] = [];
    });
  }

  onSubtitleTrackSwitch(data) {
    console.log('onSubtitleTrackSwitch');
    this.currentTrackId = data.id;
    this.clearVttFragQueues();
  }

  // Got a new set of subtitle fragments.
  onSubtitleTrackLoaded() {
    console.log('onSubtitleTrackLoaded');
    this.state = State.IDLE;
    this.tick();
  }

  // timeline-controller
  onFragLoaded(data) {
    let frag = data.frag,
      payload = data.payload;
    if (frag.type !== 'subtitle') {
      return;
    }
    // FIXED initPTS or initPDT
    // We need an initial synchronisation PTS. Store fragments as long as none has arrived.
    if ((!this.adjustWithPDT) && typeof this.initPTS === 'undefined') {
      this.unparsedVttFrags.push(data);
      return;
    }
    if (this.adjustWithPDT && typeof this.initPDT === 'undefined') {
      this.unparsedVttFrags.push(data);
      return;
    }
    if (this.hls.config.useAribSubtitleXml) {
      this._parseAribXml(frag, payload);
    }
    else {
      this._parseWebVTT(frag, payload);
    }
  }

  _parseAribXml(frag, payload) {
    //console.log('_parseAribXml', frag.relurl);
    let textTracks = this.media.textTracks,
      hls = this.hls,
      currentTrack = textTracks[frag.trackId];
    if (this.adjustWithPDT) {
      frag.start = (moment(frag.programDateTime).valueOf() - this.initPDT) / 1000;
      frag.duration = 180; // indefinite対策
    }
    else {
      // parse後に取得したptsから計算する
    }
    var parser = new AribSubtitleXmlParser(frag, payload, this.media, this.initPTS, !this.adjustWithPDT, hls.config.debug);
    parser.parse((err, cues) => {
      if (err || !cues) {
        hls.trigger(Event.SUBTITLE_FRAG_PROCESSED, {success: false, frag: frag});
        return;
      }
      cues.forEach(cue => {
        if (cue.startTime < 0) return;
        currentTrack.addCue(cue);
        if (hls.config.debug) console.dir(cue);
      });
      hls.trigger(Event.SUBTITLE_FRAG_PROCESSED, {success: true, frag: frag});
    });

  }
  _parseWebVTT(frag, payload) {
    //console.log('_parseWebVTT', frag.relurl);
    let textTracks = this.media.textTracks,
      hls = this.hls;
    if (this.adjustWithPDT) {
      frag.start = (moment(frag.programDateTime).valueOf() - this.initPDT) / 1000;
    }
    // Parse the WebVTT file contents.
    if (!this.vttCCs) {
      this.vttCCs = {ccOffset: 0, presentationOffset: 0};
      this.prevCC = frag.cc;
    }
    this.vttCCs[frag.cc] = { start: frag.start, prevCC: this.prevCC, new: true, url: frag.url};
    const syncPTS = this.hls.config.adjustWithPDT? undefined: this.initPTS;
    WebVTTParser.parse(payload, syncPTS, this.vttCCs, frag.cc, function (cues) {
      const currentTrack = textTracks[frag.trackId];
      // Add cues and trigger event with success true.
      cues.forEach(cue => {
        if (cue.startTime < 0) return;
        currentTrack.addCue(cue);
        if (hls.config.debug) console.dir(cue);
      });
      hls.trigger(Event.SUBTITLE_FRAG_PROCESSED, {success: true, frag: frag});
    },
    function (e) {
      // Something went wrong while parsing. Trigger event with success false.
      logger.log(`Failed to parse VTT cue: ${e}`);
      hls.trigger(Event.SUBTITLE_FRAG_PROCESSED, {success: false, frag: frag});
    });

  }

}
export default AribSubtitleStreamController;



// WEBPACK FOOTER //
// ./src/hlsjs/arib-subtitle-stream-controller.js